// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

import {
  isPromoted,
  useMlThresholdProfiles,
  type MlThresholdProfile,
  type MlThresholdProfileGroup,
} from './useMlThresholdProfiles';

/**
 * The tuned threshold profiles the tuner has registered, grouped by imagery provider, and the
 * buttons that put them into force.
 *
 * Promoting is the only thing on this page that changes what the platform detects, and it does
 * so without a deploy — so the numbers that argue for a profile sit next to the button rather
 * than in a JSON file on somebody's laptop.
 *
 * One button per field group, because that is how a tuning run is actually judged: after the
 * 2026-08-20 search the bunker and water values were worth shipping and the fairway and green
 * values were an overfit, and adopting half of it meant editing the analyser's source. A group
 * that lost ground on the holdout courses shows why it cannot be promoted instead of offering a
 * button that would be refused.
 */
export function MlThresholdProfilesPanel({
  apiBaseUrl,
  token,
}: {
  apiBaseUrl: string;
  token: string | null | undefined;
}) {
  const { t } = useI18n();
  const { profiles, loading, error, actionError, busyProfileId, promote, demote } =
    useMlThresholdProfiles(apiBaseUrl, token, true);

  const providers = [...new Set(profiles.map((p) => p.provider))];

  return (
    <View style={styles.card} testID="ml-threshold-profiles-panel">
      <Text style={styles.title}>{t('mlThresholdProfiles.title')}</Text>
      <Text style={styles.legend}>{t('mlThresholdProfiles.legend')}</Text>

      {actionError ? (
        <Text style={styles.error} testID="ml-threshold-profiles-action-error">
          {actionError}
        </Text>
      ) : null}

      {loading ? (
        <ActivityIndicator color={appColors.accent.primary} />
      ) : error === 'unavailable' ? (
        <Text style={styles.notice}>{t('mlThresholdProfiles.unavailable')}</Text>
      ) : error ? (
        <Text style={styles.notice}>{t('mlThresholdProfiles.failed')}</Text>
      ) : profiles.length === 0 ? (
        <Text style={styles.notice} testID="ml-threshold-profiles-empty">
          {t('mlThresholdProfiles.empty')}
        </Text>
      ) : (
        providers.map((provider) => (
          <View key={provider} style={styles.group}>
            <Text style={styles.provider}>{provider}</Text>
            {profiles
              .filter((p) => p.provider === provider)
              .map((profile) => (
                <ProfileRow
                  key={profile.id}
                  profile={profile}
                  busy={busyProfileId === profile.id}
                  onPromote={(groups) => void promote(profile.id, groups)}
                  onDemote={(groups) => void demote(profile.id, groups)}
                />
              ))}
          </View>
        ))
      )}
    </View>
  );
}

function ProfileRow({
  profile,
  busy,
  onPromote,
  onDemote,
}: {
  profile: MlThresholdProfile;
  busy: boolean;
  onPromote: (groups: string[]) => void;
  onDemote: (groups: string[]) => void;
}) {
  const { t } = useI18n();
  const promoted = isPromoted(profile);
  // Groups this run left alone say nothing about anything; showing them would bury the two or
  // three that a person actually has to decide about.
  const decidable = profile.groups.filter((group) => group.changedFields.length > 0);

  return (
    <View
      style={[styles.row, promoted ? styles.rowPromoted : null]}
      testID={`ml-threshold-profile-${profile.version}`}
    >
      <View style={styles.rowMain}>
        <Text style={styles.version}>{profile.version}</Text>
        <Text style={styles.meta}>
          {t('mlThresholdProfiles.tunedOn', {
            when: formatWhen(profile.tunedAt),
            trainCrops: profile.trainCrops,
            holdoutCrops: profile.holdoutCrops,
          })}
        </Text>
        {/* Train against holdout, both against their baseline. A profile is only worth
            promoting if it moved the courses it was not allowed to learn from. */}
        <Text style={styles.meta}>
          {t('mlThresholdProfiles.trainScore', {
            tuned: profile.trainObjective.toFixed(4),
            baseline: profile.trainBaselineObjective.toFixed(4),
          })}
        </Text>
        <Text style={styles.meta}>
          {profile.holdoutObjective === null || profile.holdoutBaselineObjective === null
            ? t('mlThresholdProfiles.noHoldout')
            : t('mlThresholdProfiles.holdoutScore', {
                tuned: profile.holdoutObjective.toFixed(4),
                baseline: profile.holdoutBaselineObjective.toFixed(4),
              })}
        </Text>
        <Text style={styles.meta}>
          {t('mlThresholdProfiles.baselineSource', { source: profile.baselineSource })}
        </Text>
        {decidable.length === 0 ? (
          <Text style={styles.meta}>{t('mlThresholdProfiles.changedNothing')}</Text>
        ) : (
          decidable.map((group) => (
            <GroupRow
              key={group.name}
              profile={profile}
              group={group}
              busy={busy}
              onPromote={() => onPromote([group.name])}
              onDemote={() => onDemote([group.name])}
            />
          ))
        )}
      </View>
    </View>
  );
}

/**
 * One field group: what it would change, and the button for it. The holdout warning sits per
 * group rather than per card, because a run is routinely half worth having.
 */
function GroupRow({
  profile,
  group,
  busy,
  onPromote,
  onDemote,
}: {
  profile: MlThresholdProfile;
  group: MlThresholdProfileGroup;
  busy: boolean;
  onPromote: () => void;
  onDemote: () => void;
}) {
  const { t } = useI18n();
  const promoted = group.promotedAt !== null;
  const id = `${profile.version}-${group.name}`;

  return (
    <View style={styles.groupRow} testID={`ml-threshold-group-${id}`}>
      <View style={styles.groupMain}>
        <Text style={styles.groupName}>
          {/* A profile registered before groups existed is one group naming no class — it is
              every gate it carries, which is what promoting it used to mean. */}
          {group.kinds.length > 0
            ? t('mlThresholdProfiles.group', { group: group.name, kinds: group.kinds.join(', ') })
            : t('mlThresholdProfiles.groupEverything')}
        </Text>
        <Text style={styles.meta}>
          {t('mlThresholdProfiles.changed', { fields: group.changedFields.join(', ') })}
        </Text>
        {group.regressesOnHoldout ? (
          <Text style={styles.warning}>{t('mlThresholdProfiles.overfitGroup')}</Text>
        ) : null}
      </View>

      <View style={styles.rowActions}>
        {promoted ? (
          <>
            <Text style={styles.badge}>{t('mlThresholdProfiles.promoted')}</Text>
            <Pressable
              accessibilityRole="button"
              disabled={busy}
              onPress={onDemote}
              style={styles.button}
              testID={`ml-threshold-profile-demote-${id}`}
            >
              <Text style={styles.buttonText}>{t('mlThresholdProfiles.demote')}</Text>
            </Pressable>
          </>
        ) : group.regressesOnHoldout ? null : (
          <Pressable
            accessibilityRole="button"
            disabled={busy}
            onPress={onPromote}
            style={styles.button}
            testID={`ml-threshold-profile-promote-${id}`}
          >
            <Text style={styles.buttonText}>{t('mlThresholdProfiles.promote')}</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

const formatWhen = (iso: string | null): string => {
  if (!iso) return '–';
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString();
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#07200D',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    padding: 14,
    gap: 10,
  },
  title: { color: appColors.text.primary, fontSize: 15, fontWeight: '700' },
  legend: { color: appColors.text.secondary, fontSize: 11 },
  notice: { color: appColors.text.secondary, fontSize: 13 },
  error: { color: appColors.status.danger, fontSize: 12 },
  group: { gap: 6 },
  provider: {
    color: appColors.text.secondary,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#0F2A15',
  },
  rowPromoted: { borderLeftWidth: 3, borderLeftColor: appColors.accent.primary, paddingLeft: 8 },
  rowMain: { flex: 1, gap: 2 },
  groupRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginTop: 6,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#0F2A15',
  },
  groupMain: { flex: 1, gap: 2 },
  groupName: { color: appColors.text.primary, fontSize: 12, fontWeight: '600' },
  rowActions: { alignItems: 'flex-end', gap: 6 },
  version: { color: appColors.text.primary, fontSize: 13, fontWeight: '700' },
  meta: { color: appColors.text.secondary, fontSize: 11 },
  warning: { color: '#E5B23B', fontSize: 11 },
  badge: {
    color: appColors.accent.primary,
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  button: {
    height: 30,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: { color: appColors.accent.primary, fontSize: 11, fontWeight: '700' },
});
